import type { ComposerTranslation } from 'vue-i18n';
import { VinceLiveUtils } from '@vincesoftware/vincelive-utils';
import type {
    ExecutionControlParameters,
    ExecutionStep, ExecutionStepError, WorkflowExecutionRequestBody, WorkflowExecutionRequestHeaders,
    WorkflowExecutionResult,
    WorkflowExecutionTimeline
} from '../types/workflow-execution';
import { AdditionalWorkflowInputDataType, type WorkflowConfig } from '../types/workflows';
import type { FormField } from '../types/trigger';
import { IGNORE_FILTER_VALUE } from '../constants/trigger';
import {
    DEFAULT_INPUT_FIELD_ID_PREFIX,
    DEFAULT_INPUTS_DATA_CONVERTERS
} from '../constants/m3';
import type { UserInfo } from 'vince-core/types/auth';
import { DataType } from 'vince-core/types/common';

export const fillTimeline = (timeline: WorkflowExecutionTimeline, executionDetails: WorkflowExecutionResult | null) => {
    const filledTimeline = JSON.parse(JSON.stringify(timeline));

    if (!filledTimeline) { return filledTimeline; }

    if (!filledTimeline.steps) {
        filledTimeline.steps = [];
    }

    if (!executionDetails) {
        return filledTimeline;
    }

    const ed = executionDetails;

    filledTimeline.executionId = ed.executionId;
    filledTimeline.startTime = ed.payload.startTime ?? ed.payload.trigger.header.scheduledTime;
    filledTimeline.endTime = ed.endTime;
    filledTimeline.duration = new Date(filledTimeline.endTime).getTime() - new Date(filledTimeline.startTime).getTime();
    filledTimeline.failed = ed.failed ?? false;

    let error: ExecutionStepError;
    if (ed.error) {
        try {
            const parsedError = JSON.parse(ed.error)! as { errorMessage: string, errorType: string};
            try {
                const parts = parsedError.errorMessage.split('|');
                error = {
                    stepId: parts[0]!,
                    type: parsedError.errorType,
                    message: parts[1]!
                };
            } catch (e) {
                error = {
                    stepId: '',
                    type: parsedError.errorType,
                    message: parsedError.errorMessage!
                };
            }
        } catch (e) {
            error = {
                stepId: '',
                type: ed.error === 'WORKFLOW_TIME_OUT' ? 'Timeout' : 'Unknown',
                message: ed.error!
            };
        }
        filledTimeline.error = error;
    }

    const history = ed.payload.result.history;

    filledTimeline.steps?.forEach((step: ExecutionStep) => {
        const stepData = history.find(h => h.stepId.endsWith(step.stepId));

        if (stepData) {
            step.success = stepData.success ?? stepData.duration > 0;
            step.duration = stepData.duration;
            step.status = step.success ? 1 : -1;
            step.successfulRecords = (stepData.data && Object.keys(stepData.data!).length) ?? undefined;
            step.recordsProcessed = step.successfulRecords;
            step.files = JSON.parse(JSON.stringify(stepData.files));
        }

        if (timeline.error?.stepId?.endsWith(step.stepId)) {
            step.error = {
                ...error,
                stepId: step.stepId // replace the full stepId with the simple id
            };
            timeline.error = step.error;
            step.success = false;
            step.status = -1;
        }
    });

    try {
        filledTimeline.triggerFile = ed.payload?.triggerFile;
    } catch {
        filledTimeline.triggerFile = null;
    }

    return filledTimeline;
};

export const buildRunHeaders = (fields: FormField[], values: WorkflowExecutionRequestBody, excludedFields: string[]): Record<string, string | number> => {
    const res = {} as Record<string, string | number>;

    for (const field of fields) {
        const inputValue = values[field.fieldId];
        if (inputValue) {
            const valueStr = inputValue.toString();
            if (field.dataType === DataType.NUMERIC || field.dataType === AdditionalWorkflowInputDataType.NUMBER) {
                if (valueStr.includes('.')) {
                    res[field.fieldId] = parseFloat(valueStr);
                } else {
                    res[field.fieldId] = valueStr.trim() === valueStr ? parseInt(valueStr) : valueStr;
                }
            } else {
                res[field.fieldId] = inputValue;
            }
        } else {
            res[field.fieldId] = '';
        }
    }

    for (const key of excludedFields) {
        res[key] = IGNORE_FILTER_VALUE;
    }

    return res;
};


export const addControlValuesFromInput = (ecb: ExecutionControlParameters, preparedValues: WorkflowExecutionRequestHeaders): ExecutionControlParameters => {
    for (const field of Object.keys(preparedValues).filter(it => it.startsWith(DEFAULT_INPUT_FIELD_ID_PREFIX))) {
        const fieldName = field.replace(DEFAULT_INPUT_FIELD_ID_PREFIX, '');

        const value = DEFAULT_INPUTS_DATA_CONVERTERS[fieldName]
            ? DEFAULT_INPUTS_DATA_CONVERTERS[fieldName](preparedValues[field]!)
            : preparedValues[field]!;

        ecb.executionParams![fieldName] = value || ecb.executionParams![fieldName];
    }

    return ecb;
};

export const prepareValues = (values: WorkflowExecutionRequestBody): WorkflowExecutionRequestBody => {
    const result: WorkflowExecutionRequestBody = {};

    for (const fieldId of Object.keys(values)) {
        if (values[fieldId] !== undefined) {
            const value = values[fieldId];
            if (typeof value === 'string') {
                // Trim normal strings as before, but preserve whitespace-only input so it counts as a real value (e.g. for mandatory fields).
                if (value.length > 0) {
                    result[fieldId] = value.trim() || value;
                }
            } else if (typeof value === 'number') {
                result[fieldId] = value;
            }
        }
    }

    return result;
};

export const getErrors = (fields: FormField[], values: WorkflowExecutionRequestBody, t: ComposerTranslation): Record<string, string> => {
    const validationResult: Record<string, string> = {};

    for (const field of fields) {
        const inputValue = values[field.fieldId];
        if (inputValue) {
            if (field.dataType === DataType.NUMERIC || field.dataType === AdditionalWorkflowInputDataType.NUMBER) {
                const numericValue = inputValue.toString().includes('.') ? parseFloat(inputValue.toString()) : parseInt(inputValue.toString());
                if (isNaN(numericValue)) {
                    validationResult[field.fieldId] = t('common.validation.notNumber');
                }
            }
            if (
                field.dataType === DataType.STRING &&
                typeof inputValue === 'string' &&
                inputValue.length > parseInt(field.length)
            ) {
                validationResult[field.fieldId] = t('common.validation.tooLong', { maxLength: field.length });
            }
        } else if ((field.customRequired || field.mandatory) && !field.valueNotRequired) {
            validationResult[field.fieldId] = t('common.validation.required');
        }
    }

    return validationResult;
};

export const getExecutionControlParameters = (executionResult: WorkflowExecutionResult, workflowConfig: WorkflowConfig): ExecutionControlParameters => {
    const connectionId = executionResult.payload?.trigger?.executionParams?.connectionId || undefined;
    const triggerFile = executionResult.payload?.triggerFile || undefined;
    return {
        logLevel: workflowConfig?.logLevel ?? 30,
        notifyOnError: workflowConfig?.notifyOnError ?? false,
        notificationEmail: workflowConfig?.notificationEmail ?? '',
        returnData: false,
        triggerFile,
        executionParams: {
            connectionId,
            retriedFrom: executionResult.executionId
        }
    };
};

export const userTags = (userInfo: UserInfo | undefined, workflowConfig: WorkflowConfig | undefined) => {
    if (!userInfo) {
        return {};
    }

    if (userInfo.tags) {
        const userTags = Object.fromEntries(
            Object.entries(userInfo.tags)
                .map(([key, val]) => [key, val ? (Array.isArray(val) ? val[0] : val) : undefined])
                .filter(([, value]) => value)
        );

        return workflowConfig?.workflowTags
            ? Object.fromEntries(
                Object.entries(workflowConfig.workflowTags ?? {}).map(([target, source]) => [target, userTags[source]])
            )
            : {};
    } else {
        return {};
    }
};

export const getDownloadFilePath = (inputSnapshotFileKey: string | undefined, triggerFile: string | undefined): string => {
    if (!inputSnapshotFileKey) {
        return triggerFile || '';
    }

    return VinceLiveUtils.hasTenantPrefix(inputSnapshotFileKey)
        ? inputSnapshotFileKey.split('/').slice(1).join('/')
        : inputSnapshotFileKey;
};


